/**
 * Praxis design tokens (WEB-5972).
 *
 * The Assistant drawer lives inside a Shadow DOM (`ShadcnScope`) and only sees
 * the stylesheet that scope injects. We append this sheet at the root of
 * `AssistantShell` so the design vars, typography and utility classes only
 * apply to the assistant — Salesboard and other shadcn surfaces keep their
 * existing tokens.
 */
const praxisStyles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&family=Source+Serif+4:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

/* Keep scrolling inside the assistant drawer from chaining into the
   underlying page/app once a scroll container hits its top or bottom. */
.praxis,
.praxis [data-radix-scroll-area-viewport],
.praxis .pr-settings-wrap,
.praxis .pr-sidebar,
.praxis .pr-chat-main { overscroll-behavior: contain; }

.praxis {
  /* Paper + ink — warm neutral, slightly softer than pure grey */
  --pr-bg: oklch(0.985 0.003 250);
  --pr-surface: oklch(1 0 0);
  --pr-surface-2: oklch(0.975 0.004 250);
  --pr-surface-3: oklch(0.955 0.005 250);
  --pr-surface-4: oklch(0.92 0.006 250);

  --pr-ink: oklch(0.22 0.012 250);
  --pr-ink-2: oklch(0.36 0.012 250);
  --pr-ink-3: oklch(0.50 0.010 250);
  --pr-ink-4: oklch(0.62 0.010 250);
  --pr-ink-5: oklch(0.78 0.006 250);

  --pr-border: oklch(0.90 0.005 250);
  --pr-border-2: oklch(0.82 0.006 250);
  --pr-hairline: oklch(0.92 0.005 250);

  /* Muted professional navy accent */
  --pr-accent: oklch(0.42 0.11 245);
  --pr-accent-hover: oklch(0.36 0.12 245);
  --pr-accent-ink: oklch(0.42 0.11 245);
  --pr-accent-soft: oklch(0.975 0.008 245);
  --pr-accent-soft-2: oklch(0.945 0.015 245);

  --pr-success: oklch(0.55 0.12 155);
  --pr-success-soft: oklch(0.96 0.025 155);
  --pr-warn: oklch(0.68 0.14 75);
  --pr-warn-soft: oklch(0.965 0.04 75);
  --pr-danger: oklch(0.58 0.18 25);
  --pr-danger-soft: oklch(0.96 0.03 25);

  --pr-shadow-sm: 0 1px 2px oklch(0.22 0.012 250 / 0.06);
  --pr-shadow-md: 0 4px 12px oklch(0.22 0.012 250 / 0.06), 0 1px 2px oklch(0.22 0.012 250 / 0.04);
  --pr-shadow-lg: 0 24px 48px -20px oklch(0.22 0.012 250 / 0.20), 0 2px 6px oklch(0.22 0.012 250 / 0.06);

  --pr-font-serif: "Source Serif 4", Georgia, "Times New Roman", serif;
  --pr-font-ui: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  --pr-font-mono: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;

  --pr-r-sm: 6px;
  --pr-r-md: 10px;
  --pr-r-lg: 14px;

  font-family: var(--pr-font-ui);
  font-feature-settings: "ss01", "cv11";
  color: var(--pr-ink);
  background: var(--pr-surface);
}

/* Header brand */
.pr-shell-header {
  height: 52px;
  padding: 0 10px 0 16px;
}
.pr-brand-dot {
  position: relative;
  width: 34px; height: 34px;
  background: var(--pr-ink);
  color: var(--pr-surface);
  display: grid; place-items: center;
  border-radius: var(--pr-r-md);
  overflow: hidden;
  font-family: var(--pr-font-serif);
  font-style: italic; font-weight: 500;
  font-size: 17px;
  letter-spacing: -0.01em;
  flex-shrink: 0;
}
.pr-brand-dot img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pr-brand-name {
  font-family: var(--pr-font-serif);
  font-size: 17px; font-weight: 500;
  color: var(--pr-ink);
  letter-spacing: -0.01em;
  line-height: 1.15;
}
.pr-brand-sub {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px; color: var(--pr-ink-4);
  margin-top: 2px;
}
.pr-brand-sub-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--pr-success);
  box-shadow: 0 0 0 3px oklch(0.55 0.12 155 / 0.2);
}

/* Header iconbuttons */
.pr-iconbtn {
  width: 34px; height: 34px;
  border-radius: var(--pr-r-sm);
  background: transparent; border: none;
  color: var(--pr-ink-3);
  display: grid; place-items: center;
  cursor: pointer;
  transition: color 120ms ease, background 120ms ease;
  flex-shrink: 0;
}
.pr-iconbtn:hover { background: var(--pr-surface-2); color: var(--pr-ink); }
.pr-iconbtn.on { background: var(--pr-ink); color: var(--pr-surface); }
.pr-iconbtn svg { width: 18px; height: 18px; stroke-width: 1.7; }

/* Shell surfaces */
.pr-shell { background: var(--pr-surface); color: var(--pr-ink); }
.pr-shell-header {
  position: relative;
  z-index: 40;
  background: var(--pr-surface);
  border-bottom: 1px solid var(--pr-hairline);
}
.pr-panel-surface { background: var(--pr-bg); }

/* Sidebar */
.pr-sidebar {
  background: var(--pr-surface);
  border-right: 1px solid var(--pr-hairline);
}
.pr-sidebar-new {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px;
  border-radius: var(--pr-r-sm);
  background: transparent; border: none;
  cursor: pointer;
  font-size: 13px; font-weight: 500;
  color: var(--pr-ink);
  transition: background 120ms ease, color 120ms ease;
  width: 100%;
  text-align: left;
  font-family: inherit;
}
.pr-sidebar-new:hover { background: var(--pr-surface-2); color: var(--pr-accent-ink); }
.pr-sidebar-new .pr-shortcut {
  margin-left: auto;
  font-family: var(--pr-font-mono); font-size: 11px;
  color: var(--pr-ink-4);
}

.pr-sb-section-head {
  width: 100%;
  display: flex; align-items: center; gap: 6px;
  padding: 10px 14px 6px 18px;
  font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--pr-ink-4);
  text-transform: uppercase;
  cursor: pointer;
  background: none; border: none;
  text-align: left;
  font-family: inherit;
  transition: color 120ms ease;
}
.pr-sb-section-head:hover { color: var(--pr-ink-3); }
.pr-sb-section-head.plain { cursor: default; }
/* Action variant used for "New chat" — same muted section-head typography
   but with a subtle hover background so it reads as a clickable action. */
.pr-sb-section-head.pr-sb-section-head--action:hover {
  background: var(--pr-surface-2);
  color: var(--pr-accent-ink);
}
/* The SquarePen glyph's optical center sits a touch above the uppercase
   text baseline. Nudge the label down so they read as horizontally aligned. */
.pr-sb-section-head.pr-sb-section-head--action > span { margin-top: 2px; }
.pr-sb-section-head .pr-chev { display: inline-flex; transition: transform 140ms ease; }
.pr-sb-section-head .pr-chev.open { transform: rotate(90deg); }

.pr-conv {
  display: flex; align-items: center; gap: 6px;
  padding: 0 14px 0 18px;
  height: 32px;
  cursor: pointer;
  color: var(--pr-ink-2);
  transition: background 80ms ease, color 80ms ease;
  position: relative;
  text-align: left;
  background: none; border: none;
  width: 100%;
  font-family: inherit;
}
.pr-conv:hover { background: var(--pr-accent-soft); }
.pr-conv.active { background: var(--pr-accent-soft); }
.pr-conv.editing,
.pr-conv.editing:hover { background: transparent; cursor: default; }
.pr-conv-icon {
  width: 11px; height: 11px;
  display: grid; place-items: center;
  color: var(--pr-ink-3);
  flex-shrink: 0;
}
.pr-conv-icon svg { width: 11px; height: 11px; stroke-width: 1.7; }
/* Date divider between messages — sticks to the top of the scroll area so
   the user always sees which day the visible messages belong to, until the
   next day's divider scrolls into view and covers it. */
.pr-date-divider {
  position: sticky;
  top: 0;
  z-index: 25;
  padding: 8px 0 6px;
}
/* Background extends full width of the scroll pane (well past the message
   column) so tooltips / focus rings can't bleed past the sticky bar at its
   edges. The element itself still only sizes to the content column. */
.pr-date-divider::before {
  content: '';
  position: absolute;
  inset: 0;
  left: -100vw;
  right: -100vw;
  background: var(--pr-surface);
  z-index: -1;
}

/* Shared sizing for row-level icons (run section headers, sched/hook headers)
   so they render identically to the sidebar conversation icons. */
.pr-row-icon {
  width: 14px; height: 14px;
  display: grid; place-items: center;
  flex-shrink: 0;
}
.pr-row-icon svg { width: 14px; height: 14px; stroke-width: 1.7; }
.pr-conv.active .pr-conv-icon { color: var(--pr-accent); }
.pr-conv-icon.unread { color: var(--pr-danger); }
.pr-conv-icon.unread svg { stroke-width: 2.5; }
.pr-unread-dot {
  display: block;
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--pr-danger);
}
/* Lucide icons in the sidebar use a lighter stroke to match the design */
.pr-sb-section-head svg,
.pr-sidebar-new svg { width: 14px; height: 14px; stroke-width: 1.7; }
.pr-sb-section-head .pr-chev svg { width: 11px; height: 11px; }
.pr-conv-text { min-width: 0; flex: 1; }
.pr-conv-title {
  font-size: 13px; color: var(--pr-ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-weight: 400;
}
.pr-conv.active .pr-conv-title { font-weight: 500; }
.pr-conv-meta {
  font-size: 11px; color: var(--pr-ink-4); margin-top: 1px;
  font-variant-numeric: tabular-nums;
  display: flex; align-items: center; gap: 8px;
}
.pr-conv-meta .pr-sched-tag { color: var(--pr-accent); font-weight: 500; }
.pr-conv-actions {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex; gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 80ms ease;
}
.pr-conv:hover .pr-conv-actions { opacity: 1; pointer-events: auto; }
/* Reserve space for actions on hover so the title ellipses cleanly
   instead of running under the buttons. */
.pr-conv:hover .pr-conv-text { padding-right: 52px; }
.pr-conv.editing .pr-conv-text,
.pr-conv.editing:hover .pr-conv-text { padding-right: 0; }
.pr-conv-actions .pr-iconbtn { width: 20px; height: 20px; }
.pr-conv-actions .pr-iconbtn svg { width: 12px; height: 12px; }
.pr-conv-actions .pr-iconbtn:hover { background: var(--pr-surface-2); color: var(--pr-ink); }
.pr-conv-actions .pr-iconbtn.pr-iconbtn-danger:hover { background: var(--pr-danger-soft); color: var(--pr-danger); }
.pr-iconbtn.pr-iconbtn-confirm,
.pr-iconbtn.pr-iconbtn-confirm:hover { background: var(--pr-danger); color: white; }
.pr-conv-edit {
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--pr-accent);
  border-radius: 0;
  padding: 0 0 1px;
  font-size: 13px;
  color: var(--pr-ink);
  outline: none;
  width: 100%;
  font-family: inherit;
}
.pr-sidebar-error {
  margin: 6px 14px 0;
  padding: 8px 10px;
  border-radius: var(--pr-r-sm);
  background: var(--pr-danger-soft);
  color: oklch(0.38 0.16 25);
  font-size: 12px;
}

.pr-unread-pill {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px; padding: 0 5px;
  border-radius: 9px;
  background: oklch(0.58 0.18 25);
  color: white;
  font-size: 10px; font-weight: 600;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  margin-left: auto;
}
.pr-unread-pill.sm {
  min-width: 14px; height: 14px;
  font-size: 9.5px;
  padding: 0 4px;
}
.pr-unread-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: oklch(0.58 0.18 25);
  flex-shrink: 0;
}
.pr-active-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--pr-success);
  flex-shrink: 0;
}
/* Hide unread dot when conv row actions are showing */
.pr-conv:hover .pr-unread-dot,
.pr-conv:hover .pr-conv-unread { display: none; }

/* Agent glyphs (collapsed sidebar) — borderless, coloured icon only */
.pr-agent-glyph {
  position: relative;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  border: none;
  border-radius: var(--pr-r-sm);
  background: transparent;
  cursor: pointer;
  transition: background 120ms ease, opacity 120ms ease;
  padding: 0;
}
.pr-agent-glyph:hover { background: var(--pr-surface-2); }
.pr-agent-glyph.active { background: var(--pr-accent-soft); }
.pr-agent-glyph svg { width: 18px; height: 18px; stroke-width: 1.6; }
.pr-agent-chip-dot {
  position: absolute;
  top: 3px; right: 3px;
  width: 6px; height: 6px;
  background: oklch(0.58 0.18 25);
  border-radius: 50%;
  border: 1px solid var(--pr-surface);
}

/* Chat surface */
.pr-chat-main { background: var(--pr-surface); }

.pr-avatar-sm {
  width: 28px; height: 28px;
  border-radius: 7px;
  background: var(--pr-ink);
  color: white;
  display: grid; place-items: center;
  flex-shrink: 0;
  overflow: hidden;
  font-family: var(--pr-font-serif);
  font-weight: 600;
  font-size: 13px;
}
.pr-avatar-sm img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* When an image is present, drop the dark ink fallback background so it
   can't show as an anti-aliased rim around the rounded corners. */
.pr-avatar-sm:has(img) { background: transparent; }
.pr-brand-dot:has(img) { background: transparent; }
.pr-avatar-lg:has(img) { background: transparent; }

.pr-assistant-name {
  font-size: 13px; font-weight: 600; color: var(--pr-ink);
}
.pr-msg-time {
  font-family: var(--pr-font-mono); font-size: 11px;
  color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
}

/* Prose (serif headings, monospace code, accent quotes) */
.pr-prose { font-size: 14px; line-height: 1.65; color: var(--pr-ink); }
.pr-prose > *:first-child { margin-top: 0; }
.pr-prose > *:last-child { margin-bottom: 0; }
.pr-prose p { margin: 8px 0; }
.pr-prose h1, .pr-prose h2 {
  font-family: var(--pr-font-serif);
  font-weight: 500; letter-spacing: -0.01em; color: var(--pr-ink);
}
.pr-prose h1 { font-size: 20px; margin: 18px 0 8px; line-height: 1.25; }
.pr-prose h2 { font-size: 17px; margin: 16px 0 6px; line-height: 1.3; }
.pr-prose h3 {
  font-size: 11.5px; margin: 14px 0 6px;
  font-family: var(--pr-font-ui);
  letter-spacing: 0.04em; font-weight: 600;
  color: var(--pr-ink-3);
  text-transform: uppercase;
}
.pr-prose ul, .pr-prose ol { padding-left: 0; margin: 8px 0; list-style: none; }
.pr-prose ul li, .pr-prose ol li { margin: 4px 0; }
.pr-prose ul li { position: relative; padding-left: 18px; }
.pr-prose ul li::before {
  content: ""; position: absolute; left: 2px; top: 0.72em;
  width: 6px; height: 1px; background: var(--pr-ink-3);
}
.pr-prose ol { counter-reset: item; }
.pr-prose ol li {
  counter-increment: item;
  position: relative; padding-left: 24px;
}
.pr-prose ol li::before {
  content: counter(item) ".";
  position: absolute; left: 0; top: 0;
  font-family: var(--pr-font-mono); font-variant-numeric: tabular-nums;
  font-size: 12px; color: var(--pr-accent);
  font-weight: 500;
}
.pr-prose code {
  font-family: var(--pr-font-mono); font-size: 12.5px;
  background: var(--pr-surface-2); padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid var(--pr-hairline);
  color: var(--pr-ink);
}
/* Code blocks render as a dark "editor window". A bare <pre> (inbox previews,
   embedded drawer) gets the dark body; where MessageBlock wraps the block in
   .pr-code it also gains the chrome bar (window dots, language pill, copy). */
.pr-prose pre {
  font-family: var(--pr-font-mono); font-size: 12.5px; line-height: 1.6;
  background: oklch(0.21 0.014 255); color: oklch(0.90 0.012 255);
  border: 1px solid oklch(0.30 0.02 255);
  padding: 14px 16px; border-radius: var(--pr-r-md);
  overflow-x: auto; margin: 12px 0;
  box-shadow: var(--pr-shadow-sm);
  -webkit-font-smoothing: antialiased;
}
.pr-prose pre code { background: transparent; padding: 0; border: none; color: inherit; font-size: inherit; }

.pr-prose .pr-code {
  /* Light frame matching the diff card (.pr-diff) so both code surfaces read as
     one component family (WEB-7821). */
  margin: 12px 0;
  border: 1px solid oklch(0.80 0.012 255);
  border-radius: var(--pr-r-md, 10px);
  overflow: hidden;
  background: var(--pr-surface, oklch(1 0 0));
  box-shadow: var(--pr-shadow-sm, 0 1px 2px oklch(0.22 0.012 250 / 0.06));
}
.pr-prose .pr-code pre {
  margin: 0; border: none; border-radius: 0; box-shadow: none; max-height: 360px; overflow: auto;
  background: var(--pr-surface, oklch(1 0 0));
  color: oklch(0.35 0.015 255);
  font-size: 12.5px; line-height: 1.55;
  padding: 10px 12px;
}
.pr-code-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px;
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  border-bottom: 1px solid oklch(0.80 0.012 255);
}
.pr-code-dots { display: inline-flex; gap: 6px; }
.pr-code-dots i { width: 10px; height: 10px; border-radius: 50%; }
.pr-code-dots i:nth-child(1) { background: #ff5f57; }
.pr-code-dots i:nth-child(2) { background: #febc2e; }
.pr-code-dots i:nth-child(3) { background: #28c840; }
.pr-code-lang {
  font-family: var(--pr-font-mono, ui-monospace, Menlo, monospace); font-size: 10.5px; font-weight: 500;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--pr-ink-3, oklch(0.50 0.010 250));
}
.pr-code-copy {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 5px;
  background: transparent;
  border: 1px solid var(--pr-border-2, oklch(0.82 0.006 250));
  color: var(--pr-ink-3, oklch(0.50 0.010 250));
  font-family: var(--pr-font-ui, "Inter", -apple-system, Helvetica, Arial, sans-serif); font-size: 11px; font-weight: 500;
  padding: 3px 9px; border-radius: 6px; cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
}
.pr-code-copy svg { width: 12px; height: 12px; }
.pr-code-copy:hover {
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  color: var(--pr-ink, oklch(0.22 0.012 250));
  border-color: var(--pr-ink-4, oklch(0.62 0.010 250));
}

/* Editor-style change view for a fenced diff block (WEB-7757): a bordered card
   with a title bar (window dots + "N lines changed"), collapsed behind that bar
   by default and expanding to red/green line tints. Rules are GLOBAL (not
   .pr-prose-scoped) so the frame renders wherever the block appears — the tint
   rules were already global, which is why they showed but the frame didn't. */
/* Dual selectors (.pr-prose .pr-diff … , .pr-diff …): the .pr-prose-scoped one is
   (0,2,x) so it beats the generic .pr-prose pre / .pr-prose code editor styles
   regardless of source order; the bare one covers a diff rendered outside prose.
   Mirrors how .pr-code resets its inner pre. */
.pr-prose .pr-diff,
.pr-diff {
  /* var() fallbacks are mandatory here: the Pages authoring chat renders the
     diff OUTSIDE a .praxis ancestor, so the --pr-* tokens are undefined there.
     Without fallbacks border-radius/background collapse to initial values →
     square corners + transparent header (WEB-7757). */
  margin: 12px 0;
  border: 1px solid oklch(0.80 0.012 255);
  border-radius: var(--pr-r-md, 10px);
  overflow: hidden;
  background: var(--pr-surface, oklch(1 0 0));
  box-shadow: var(--pr-shadow-sm, 0 1px 2px oklch(0.22 0.012 250 / 0.06));
}
.pr-diff-summary {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; cursor: pointer; user-select: none;
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  font-family: var(--pr-font-ui, "Inter", -apple-system, Helvetica, Arial, sans-serif); font-size: 12px; font-weight: 500;
  color: var(--pr-ink, oklch(0.22 0.012 250)); list-style: none;
}
.pr-diff-summary::-webkit-details-marker { display: none; }
.pr-diff-summary:hover { background: oklch(0.95 0.005 255); }
.pr-diff-summary:focus, .pr-diff-summary:focus-visible { outline: none; }
.pr-diff[open] .pr-diff-summary { border-bottom: 1px solid oklch(0.80 0.012 255); }
.pr-diff .pr-code-dots { display: inline-flex; gap: 6px; flex: none; }
.pr-diff .pr-code-dots i { width: 9px; height: 9px; border-radius: 50%; }
.pr-diff .pr-code-dots i:nth-child(1) { background: #ff5f57; }
.pr-diff .pr-code-dots i:nth-child(2) { background: #febc2e; }
.pr-diff .pr-code-dots i:nth-child(3) { background: #28c840; }
.pr-diff-caret {
  width: 0; height: 0; flex: none;
  border-top: 4px solid transparent; border-bottom: 4px solid transparent;
  border-left: 5px solid oklch(0.60 0.02 255);
  transition: transform 120ms ease;
}
.pr-diff[open] .pr-diff-caret { transform: rotate(90deg); }
.pr-diff-title { color: oklch(0.45 0.02 255); }
/* Full-rewrite badge — flags a whole-file rewrite (vs a targeted edit) so it's
   obvious the moment the agent commits to one. Amber = attention, not error (WEB-7756). */
.pr-diff-tag {
  flex: none; padding: 1px 7px; border-radius: 999px;
  font-size: 10px; font-weight: 600; line-height: 16px; letter-spacing: 0.02em;
  text-transform: uppercase;
  color: oklch(0.42 0.11 65); background: oklch(0.93 0.06 75);
}
/* New-page variant — a first build is expected, so it's neutral/informational
   rather than the amber attention of an unexpected full rewrite (WEB-7756). */
.pr-diff-tag--new {
  color: oklch(0.42 0.03 255); background: oklch(0.92 0.015 255);
}
.pr-diff-body {
  padding: 0;
  background: var(--pr-surface, oklch(1 0 0)); color: var(--pr-ink, oklch(0.22 0.012 250));
  /* Default shows ~10 lines then scrolls — a full rewrite is hundreds of lines
     and shouldn't dominate the thread. Drag the .pr-diff-resize handle to grow. */
  max-height: 210px; overflow: auto;
}
/* Full-width drag handle across the card bottom, with a centred grip pill so
   it reads as draggable. Drives handleDiffResizeStart (WEB-7757). */
.pr-diff-resize {
  height: 11px; cursor: ns-resize;
  display: flex; align-items: center; justify-content: center;
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  border-top: 1px solid oklch(0.90 0.008 255);
}
.pr-diff-grip {
  width: 30px; height: 3px; border-radius: 2px;
  background: oklch(0.78 0.014 255);
  transition: background 120ms ease, width 120ms ease;
}
.pr-diff-resize:hover .pr-diff-grip { background: #1D3D48; width: 40px; }
/* Each diff row is a flex pair: a fixed, neutral line-number gutter + the code
   text. The gutter is user-select:none so copying the block never drags numbers
   along; add/del tinting lives on the text so the gutter stays calm (WEB-7821). */
.pr-diff-line {
  display: flex; align-items: flex-start;
  font-family: var(--pr-font-mono, "JetBrains Mono", ui-monospace, Menlo, monospace); font-size: 12.5px; line-height: 1.55;
  color: oklch(0.50 0.015 255);
}
.pr-diff-num {
  flex: none; width: 2.75em; padding: 1px 10px 1px 0; text-align: right;
  color: oklch(0.74 0.012 255); user-select: none; -webkit-user-select: none;
}
.pr-diff-text {
  flex: 1 1 auto; min-width: 0;
  white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere;
  padding: 1px 12px 1px 4px;
}
.pr-diff-line.pr-diff-add .pr-diff-text { background: oklch(0.93 0.06 150); color: oklch(0.38 0.09 150); }
.pr-diff-line.pr-diff-del .pr-diff-text { background: oklch(0.94 0.05 25); color: oklch(0.45 0.13 25); }
/* Divider between edit hunks — a faint dashed rule so several scattered changes in
   one turn read as separate blocks, not one merged wall (WEB-7756). */
.pr-diff-line.pr-diff-sep {
  display: block; padding: 0; height: 0; margin: 12px 12px;
  border-top: 1px dashed oklch(0.82 0.012 255);
}
.pr-code.is-copied .pr-code-copy { color: oklch(0.78 0.15 155); border-color: oklch(0.50 0.12 155); }


/* While a reply streams, the in-progress code/diff card pulses instead of
   dangling the block caret beneath it, and hides the copy button until done.
   Covers .pr-diff too, else the caret ▊ leaks below the diff card and doubles
   up with the streaming dots (WEB-7757). */
.pr-asst-body.pr-caret:has(.pr-code:last-child)::after,
.pr-asst-body.pr-caret:has(.pr-diff:last-child)::after { display: none; }
.pr-caret .pr-code:last-child { border-color: oklch(0.50 0.10 245 / 0.6); }
.pr-caret .pr-code:last-child .pr-code-copy { display: none; }
.pr-caret .pr-code:last-child .pr-code-bar::after {
  content: ""; margin-left: auto;
  width: 7px; height: 7px; border-radius: 50%;
  background: oklch(0.64 0.14 245);
  animation: pr-codepulse 1.1s ease-in-out infinite;
}
@keyframes pr-codepulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.7); }
}
.pr-prose blockquote {
  border-left: 2px solid var(--pr-accent);
  padding: 2px 0 2px 14px; margin: 12px 0;
  color: var(--pr-ink-2);
  font-family: var(--pr-font-serif); font-style: italic;
  font-size: 15px; line-height: 1.55;
}
.pr-prose a {
  color: var(--pr-accent-ink);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  text-decoration-color: var(--pr-accent-soft-2);
  cursor: pointer;
  transition: text-decoration-color 120ms ease;
}
.pr-prose a:hover { text-decoration-color: var(--pr-accent); }
.pr-prose hr { border: none; border-top: 1px solid var(--pr-hairline); margin: 14px 0; }
.pr-prose img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 10px 0;
  border-radius: 8px;
  border: 1px solid var(--pr-hairline);
  cursor: zoom-in;
  transition: opacity 120ms ease;
}
.pr-prose img:hover { opacity: 0.9; }
.pr-prose table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 13px; }
.pr-prose th, .pr-prose td { border-bottom: 1px solid var(--pr-hairline); padding: 8px 10px; text-align: left; border-top: none; border-left: none; border-right: none; }
.pr-prose th { font-weight: 600; color: var(--pr-ink-3); font-size: 11.5px; letter-spacing: 0.02em; background: transparent; }
.pr-prose td { font-variant-numeric: tabular-nums; }
.pr-prose a.pr-entity-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 0 5px; margin: 0;
  border-radius: 4px;
  background: var(--pr-accent-soft);
  color: var(--pr-accent-ink);
  border: 1px solid var(--pr-accent-soft-2);
  font-size: 13px; font-weight: 500;
  line-height: 1.4;
  text-decoration: none;
  vertical-align: baseline;
  transition: background 120ms ease;
}
.pr-prose a.pr-entity-chip:hover { background: var(--pr-accent-soft-2); }
.pr-prose a.pr-mention-link { cursor: pointer; }
/* Colour swatch rendered inline before a hex code in agent output. */
.pr-prose .pr-color-swatch {
  display: inline-block;
  width: 1.05em; height: 1.05em;
  margin-right: 0.4em;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  vertical-align: -0.2em;
}

.pr-asst-msg { position: relative; }
.pr-asst-head {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 4px 0;
  margin-bottom: 4px;
}
.pr-asst-head .pr-avatar-sm { margin-top: -2px; }
.pr-asst-body {
  /* Flush-left: body, code and the action row all share the avatar's left margin
     rather than indenting under the name — nothing hangs out to the side (WEB-7756). */
  padding-left: 0;
}

/* No-name messages (e.g. page-authoring chat): lay avatar + body side-by-side
   so the first line of body content sits on the avatar row instead of
   dropping below it. */
.pr-asst-msg--inline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.pr-asst-msg--inline .pr-asst-head {
  padding: 0;
  margin-bottom: 0;
  flex-shrink: 0;
}
.pr-asst-msg--inline .pr-asst-head .pr-avatar-sm { margin-top: 0; }
.pr-asst-msg--inline .pr-asst-body {
  flex: 1;
  min-width: 0;
  padding-left: 0;
}

/* User bubble */
.pr-user-bubble {
  background: var(--pr-accent-soft);
  color: var(--pr-ink);
  border: 1px solid var(--pr-accent-soft-2);
  border-radius: var(--pr-r-lg) var(--pr-r-lg) 4px var(--pr-r-lg);
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.55;
  /* Don't let long unbroken tokens push the bubble past its max-width. */
  overflow-wrap: anywhere;
}
.pr-user-bubble > p { margin: 0; }
.pr-user-bubble > p + p { margin-top: 8px; }
.pr-user-bubble a {
  display: inline;
  padding: 1px 6px;
  margin: 0 1px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.07);
  color: inherit;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 120ms ease;
}
.pr-user-bubble a:hover { background: rgba(0, 0, 0, 0.12); }

/* Non-image attachment chips shown above the user bubble */
.pr-msg-attachment-link { text-decoration: none; color: inherit; }
.pr-msg-attachment {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 10px 6px 6px;
  background: var(--pr-surface);
  border: 1px solid var(--pr-border);
  border-radius: 10px;
  max-width: 240px;
}
.pr-msg-attachment .ws-ctx-thumb {
  width: 32px; height: 32px; border-radius: 6px;
  display: grid; place-items: center;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.04em; color: white;
  flex-shrink: 0;
}
.pr-msg-attachment .ws-ctx-thumb.pdf { background: #d44b4b; }
.pr-msg-attachment .ws-ctx-thumb.xlsx { background: #2c8e5f; }
.pr-msg-attachment .ws-ctx-thumb.image { background: #6f6ee0; }
.pr-msg-attachment .ws-ctx-thumb.pptx { background: #c75b3a; }
.pr-msg-attachment .ws-ctx-thumb.text { background: #6b7280; }
.pr-msg-attachment-info { display: flex; flex-direction: column; min-width: 0; }
.pr-msg-attachment-name {
  font-size: 12.5px; font-weight: 500; color: var(--pr-ink);
  white-space: nowrap; text-overflow: ellipsis; overflow: hidden;
}
.pr-msg-attachment-size { font-size: 11px; color: var(--pr-ink-4); }

/* Feedback */
.pr-feedback { display: flex; align-items: center; gap: 2px; }
/* Offset the row so the first icon's visible glyph — not the button's
   padded hit area — aligns with the message text above. 42px avatar+gap
   minus the button's inner 7px of padding. */
.pr-feedback-row { margin-left: 0; }
.pr-fbtn {
  background: transparent; border: none;
  width: 34px; height: 34px; border-radius: 7px;
  display: grid; place-items: center;
  color: var(--pr-ink-5);
  cursor: pointer;
  transition: all 120ms ease;
}
.pr-fbtn:hover { color: var(--pr-ink); background: var(--pr-surface-2); }
.pr-fbtn.active { color: var(--pr-accent); background: var(--pr-accent-soft); }
.pr-feedback-divider { width: 1px; height: 12px; background: var(--pr-hairline); margin: 0 4px; }

/* Thinking dots */
.pr-thinking { display: flex; align-items: flex-start; gap: 10px; max-width: 760px; margin: 0 auto; padding: 0 28px; }
/* Inline variant — drops the self-centering so it inherits the parent column
   (used inside RunSection, where MessageThread already centres the 720px track). */
.pr-thinking--inline { max-width: none; margin: 0; padding: 0; gap: 10px; }
.pr-thinking .pr-avatar-sm { opacity: 0.55; margin-top: -2px; }
.pr-thinking-body { display: flex; flex-direction: column; gap: 6px; padding-top: 6px; }
.pr-dots { display: inline-flex; gap: 4px; align-items: center; }
.pr-dots span {
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--pr-ink-3);
  animation: pr-bounce 1.2s infinite ease-in-out;
}
.pr-dots span:nth-child(2) { animation-delay: 150ms; }
.pr-dots span:nth-child(3) { animation-delay: 300ms; }
@keyframes pr-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-3px); opacity: 1; }
}
.pr-tool-status {
  font-size: 12px; color: var(--pr-ink-3);
  display: inline-flex; align-items: center; gap: 8px;
}
.pr-tool-status .pr-spin {
  width: 10px; height: 10px;
  border: 1.5px solid var(--pr-accent); border-top-color: transparent;
  animation: pr-spin 600ms linear infinite;
  border-radius: 50%;
}
@keyframes pr-spin { to { transform: rotate(360deg); } }
.pr-caret::after {
  content: "";
  display: inline-block;
  width: 7px; height: 14px;
  background: var(--pr-ink);
  margin-left: 2px; vertical-align: -2px;
  animation: pr-blink 1.06s ease-in-out infinite;
  border-radius: 1px;
}
@keyframes pr-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.15; } }

/* Composer */
.pr-composer-wrap {
  padding: 8px 28px 14px;
  background: var(--pr-surface);
  flex-shrink: 0;
  border-top: 1px solid var(--pr-hairline);
}
.pr-composer { max-width: 760px; margin: 0 auto; }
.pr-composer-box {
  position: relative;
  display: flex; flex-direction: column; gap: 2px;
  padding: 8px 10px 4px;
  background: var(--pr-surface);
  border: 1px solid var(--pr-border);
  border-radius: var(--pr-r-lg);
  transition: border-color 150ms ease, box-shadow 150ms ease;
}
.pr-composer-box.compact {
  flex-direction: row; align-items: center; gap: 8px;
  padding: 4px 6px 4px 10px;
}
.pr-composer-box.focused {
  border-color: var(--pr-accent);
  box-shadow: 0 0 0 3px var(--pr-accent-soft-2);
}
.pr-composer-box.drag {
  border-style: dashed; border-color: var(--pr-accent);
  background: var(--pr-accent-soft);
}
.pr-composer-tool {
  width: 30px; height: 30px; border-radius: var(--pr-r-sm);
  border: none; background: transparent; color: var(--pr-ink-3);
  display: grid; place-items: center; cursor: pointer;
  transition: color 120ms ease, background 120ms ease;
}
.pr-composer-tool:hover { color: var(--pr-ink); background: var(--pr-surface-2); }
.pr-composer-textarea {
  width: 100%; resize: none; outline: none; border: none; background: transparent;
  font-family: var(--pr-font-ui); font-size: 14px; line-height: 1.45;
  color: var(--pr-ink);
  padding: 2px 2px;
  max-height: 240px; min-height: 30px;
}
.pr-composer-box.compact .pr-composer-textarea {
  flex: 1; width: auto; font-size: 14px; line-height: 1.4;
  padding: 4px 2px;
  max-height: 200px; min-height: 20px;
}
.pr-composer-actions {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px;
}
.pr-composer-actions-left, .pr-composer-actions-right {
  display: flex; align-items: center; gap: 4px;
}
.pr-composer-textarea::placeholder { color: var(--pr-ink-4); }
.pr-composer-counter {
  font-family: var(--pr-font-mono); font-size: 11px;
  color: var(--pr-ink-4);
  padding: 0 6px;
  user-select: none;
  flex-shrink: 0;
}
.pr-send-btn {
  width: 30px; height: 30px; border-radius: var(--pr-r-sm);
  background: #1d3d48; color: #e8f9f8;
  border: none; display: grid; place-items: center; cursor: pointer;
  transition: background 150ms ease;
  flex-shrink: 0;
}
.pr-send-btn:hover { background: #16323b; }
.pr-send-btn:disabled { background: var(--pr-surface-4); color: var(--pr-ink-4); cursor: not-allowed; }
.pr-composer-hints {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 2px 0;
  font-size: 11px; color: var(--pr-ink-4);
}
.pr-composer-hints kbd {
  font-family: var(--pr-font-mono); font-size: 10.5px;
  background: var(--pr-surface-2);
  border: 1px solid var(--pr-border);
  border-bottom-width: 1.5px;
  border-radius: 4px;
  padding: 1px 5px;
  color: var(--pr-ink-3);
}

/* Empty state */
.pr-empty {
  flex: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 28px;
  text-align: center;
  max-width: 720px; margin: 0 auto; width: 100%;
}
.pr-empty-hero {
  width: 48px; height: 48px;
  border-radius: var(--pr-r-md);
  background: var(--pr-ink);
  display: grid; place-items: center; color: var(--pr-surface);
  margin-bottom: 20px;
  font-family: var(--pr-font-serif); font-style: italic;
  font-weight: 500; font-size: 22px;
  box-shadow: var(--pr-shadow-md);
}
.pr-empty-hero img { width: 100%; height: 100%; border-radius: var(--pr-r-md); object-fit: cover; }
.pr-empty .pr-greeting {
  font-size: 12px; color: var(--pr-ink-4);
  margin-bottom: 10px;
  font-weight: 400;
}
.pr-empty h1 {
  font-family: var(--pr-font-serif);
  margin: 0; font-size: 30px; font-weight: 500; letter-spacing: -0.02em; color: var(--pr-ink);
  line-height: 1.2;
  max-width: 520px;
}
.pr-empty p {
  margin: 12px 0 0; font-size: 14px; color: var(--pr-ink-3);
  max-width: 440px; line-height: 1.6;
}
.pr-suggestions {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  margin-top: 30px; max-width: 620px; width: 100%;
}
.pr-suggestion {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px;
  background: var(--pr-surface);
  border: 1px solid var(--pr-border);
  border-radius: var(--pr-r-md);
  cursor: pointer;
  text-align: left;
  transition: all 150ms ease;
  color: var(--pr-ink);
  font-size: 13px;
  font-family: inherit;
}
.pr-suggestion:hover {
  border-color: var(--pr-ink-4);
  box-shadow: var(--pr-shadow-sm);
  transform: translateY(-1px);
}
.pr-suggestion-icon {
  width: 28px; height: 28px;
  border-radius: var(--pr-r-sm);
  background: var(--pr-accent-soft);
  color: var(--pr-accent);
  display: grid; place-items: center;
  flex-shrink: 0;
}
.pr-suggestion-body { min-width: 0; flex: 1; }
.pr-suggestion-body strong { font-weight: 600; font-size: 13px; color: var(--pr-ink); display: block; }
.pr-suggestion-body .pr-sub { display: block; font-size: 12px; color: var(--pr-ink-4); margin-top: 3px; line-height: 1.5; }

/* Action card */
.pr-action-card {
  margin: 0;
  border: 1px solid var(--pr-hairline);
  border-radius: 3px;
  background: var(--pr-surface);
  overflow: hidden;
  transition: opacity 180ms ease;
}
.pr-action-card.resolved { opacity: 0.7; }
.pr-action-card-head {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  background: var(--pr-surface-2);
  border-bottom: 1px solid var(--pr-hairline);
  font-size: 12px;
}
.pr-action-tag {
  font-family: var(--pr-font-mono);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 2px;
  border: 1px solid currentColor;
  line-height: 1.4;
  flex-shrink: 0;
}
.pr-action-tag-create { color: oklch(0.42 0.11 155); }
.pr-action-tag-update { color: var(--pr-accent); }
.pr-action-tag-delete { color: oklch(0.48 0.16 25); }
.pr-action-card-entity {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  font-weight: 500;
}
.pr-action-card-status {
  margin-left: auto;
  font-size: 11px;
  color: var(--pr-success);
  font-weight: 500;
}
.pr-action-card-status.muted { color: var(--pr-ink-4); }
.pr-action-card-body { padding: 14px 16px 12px; }
.pr-action-card-title {
  font-family: var(--pr-font-serif);
  font-size: 16px; font-weight: 500;
  color: var(--pr-ink);
  letter-spacing: -0.005em;
}
.pr-action-card-quote {
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--pr-surface-2);
  border-left: 2px solid var(--pr-ink-5);
  font-size: 13px; line-height: 1.55;
  white-space: pre-wrap;
  color: var(--pr-ink-2);
  font-family: var(--pr-font-serif);
  font-style: italic;
}
.pr-action-card-foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--pr-hairline);
  background: var(--pr-surface);
}

/* Bulk card (multi-action) */
.pr-bulk-card {
  margin: 4px 0;
  border: 1px solid var(--pr-hairline);
  border-radius: 3px;
  background: var(--pr-surface);
  overflow: hidden;
  width: 100%;
}
.pr-bulk-card.resolved { opacity: 0.7; }
.pr-bulk-card-head {
  display: flex; flex-direction: column;
  padding: 12px 16px;
  background: var(--pr-surface);
}
.pr-bulk-card-head-row {
  display: flex; align-items: center; gap: 10px;
}
.pr-bulk-card-entity {
  font-size: 14px; color: var(--pr-ink); font-weight: 500;
}
.pr-bulk-card-count {
  margin-left: auto;
  font-size: 13px; color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
}
.pr-bulk-card-summary {
  font-size: 13px; color: var(--pr-ink-2); line-height: 1.45;
  margin-top: 6px;
}
.pr-bulk-card-body {
  padding: 6px 16px 14px;
  display: flex; flex-direction: column; gap: 14px;
}
.pr-bulk-section-label {
  font-family: var(--pr-font-ui);
  font-size: 11.5px; font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pr-ink-2);
}
.pr-bulk-section-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 10px;
}
/* The "what we're acting on" section (filter / selected ids) and the
 * "what we're setting" section (properties) read as the same thing at a
 * glance otherwise — same label style as the property row keys. Tint the
 * filter section background and use a slightly different label colour so
 * the two halves of a bulk action are visually distinct. */
.pr-bulk-section-filter {
  background: var(--pr-surface-2);
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0 -2px;
}
.pr-bulk-section-filter .pr-bulk-section-label {
  color: var(--pr-info, #3c6db6);
}
.pr-bulk-section-properties .pr-bulk-section-label {
  color: var(--pr-ok, #2f7a4e);
}
.pr-bulk-add-inline {
  display: inline-flex; align-items: center;
  padding: 3px 10px;
  font-size: 12px;
  color: var(--pr-ink-4);
  background: transparent;
  border: 1px dashed var(--pr-hairline);
  border-radius: 999px;
  cursor: pointer;
  font-family: var(--pr-font-ui);
}
.pr-bulk-add-inline:hover { color: var(--pr-ink); border-color: var(--pr-ink-4); }
.pr-bulk-chips {
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
}
.pr-bulk-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 4px 4px 10px;
  background: var(--pr-surface-2);
  border: 1px solid var(--pr-hairline);
  border-radius: 999px;
  font-size: 12.5px;
  color: var(--pr-ink);
  font-variant-numeric: tabular-nums;
}
.pr-bulk-chip-x {
  display: inline-grid; place-items: center;
  width: 18px; height: 18px;
  border: none; background: transparent;
  color: var(--pr-ink-4);
  cursor: pointer;
  border-radius: 50%;
  padding: 0; margin-left: 2px;
}
.pr-bulk-chip-x:hover { background: var(--pr-surface-3); color: var(--pr-ink); }
.pr-bulk-props {
  display: flex; flex-direction: column;
}
.pr-bulk-section--divided { border-top: 1px solid var(--pr-hairline); padding-top: 14px; }
.pr-bulk-prop-row {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid var(--pr-hairline);
}
.pr-bulk-prop-row:last-child { border-bottom: none; }
.pr-bulk-prop-k {
  color: var(--pr-ink-4);
  font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  flex-shrink: 0;
}
.pr-bulk-prop-eq { display: none; }
.pr-bulk-prop-v {
  color: var(--pr-ink);
  min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.pr-bulk-prop-actions {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
  margin-right: auto;
  visibility: hidden;
}
.pr-bulk-prop-row:hover .pr-bulk-prop-actions { visibility: visible; }
.pr-bulk-prop-v-btn {
  border: none; background: transparent; padding: 0;
  font: inherit; text-align: left; cursor: text;
  color: var(--pr-ink);
  border-radius: 2px;
}
.pr-bulk-prop-v-btn:hover { background: var(--pr-surface-2); box-shadow: 0 0 0 3px var(--pr-surface-2); }
.pr-bulk-prop-x {
  display: inline-grid; place-items: center;
  width: 22px; height: 22px;
  border: none; background: transparent;
  color: var(--pr-ink-4);
  cursor: pointer;
  border-radius: 4px;
}
.pr-bulk-prop-x:hover { background: var(--pr-surface-2); color: var(--pr-ink); }
.pr-bulk-add-btn {
  border: none; background: transparent;
  padding: 4px 6px;
  font-size: 12.5px;
  color: var(--pr-accent);
  cursor: pointer;
  font-family: var(--pr-font-ui);
  border-radius: 3px;
}
.pr-bulk-add-btn:hover { background: var(--pr-accent-soft); }
.pr-bulk-card-foot {
  display: flex; justify-content: flex-end; align-items: center;
  gap: 8px;
  padding: 10px 14px 12px;
  border-top: 1px solid var(--pr-hairline);
  background: var(--pr-surface);
}

/* Action-tag pills (bulk card) */
.pr-action-tag-pill {
  display: inline-flex; align-items: center;
  font-size: 11.5px; font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0;
  text-transform: none;
  color: var(--pr-surface);
  background: var(--pr-ink);
}
.pr-action-tag-pill-create { background: oklch(0.42 0.12 155); }
.pr-action-tag-pill-update { background: var(--pr-ink); }
.pr-action-tag-pill-delete { background: oklch(0.50 0.17 25); }

/* Write proposal group (multiple proposals collapsed into one accordion) */
.pr-wgroup {
  margin-left: 44px;
  border: 1px solid var(--pr-hairline);
  border-radius: 3px;
  background: var(--pr-surface);
  overflow: hidden;
}
.pr-wgroup-head {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  background: var(--pr-surface-2);
  border-bottom: 1px solid var(--pr-hairline);
  font-size: 12px;
}
.pr-wgroup-title {
  font-family: var(--pr-font-mono);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pr-ink-3);
  font-weight: 600;
}
.pr-wgroup-count {
  font-size: 11px;
  color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
  padding: 1px 6px;
  border: 1px solid var(--pr-hairline);
  border-radius: 999px;
}
.pr-wgroup-summary {
  margin-left: auto;
  font-size: 11.5px;
  color: var(--pr-ink-4);
}

/* Chat-surface group wrapper: keeps the amber "Proposals to review" header
 * as an attention marker but drops the tinted background + border that made
 * the group read as a card-within-a-card. */
.pr-wgroup-shell {
  display: flex; flex-direction: column; gap: 10px;
  margin: 18px 0;
}
/* Keep the expanded card panel-white so the body doesn't visually invert
 * against the title head (which inherits the panel colour). */
.pr-wgroup-shell-list > .ws-prop-card--expanded {
  background: var(--ws-panel);
}
.pr-wgroup-shell-head {
  display: inline-flex; align-items: center; gap: 6px;
  color: var(--ws-warn, #cf8c34);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.pr-wgroup-shell-head::before {
  content: "";
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--ws-warn, #cf8c34);
  box-shadow: 0 0 0 3px rgba(207, 140, 52, 0.18);
}
.pr-wgroup-shell-head .pr-wgroup-summary { color: var(--pr-ink-4); }
.pr-wgroup-shell-list {
  display: flex; flex-direction: column; gap: 10px;
}
.pr-wgroup-shell-list > .ws-prop-card,
.pr-wgroup-shell-list .ws-prop-card:last-child {
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  overflow: hidden;
}

/* Email draft — full-bordered card so a standalone email proposal reads as
 * a self-contained card (matching grouped write proposals). */
.pr-email-card-wrapper { margin: 12px 0; }
.pr-email-card-wrapper > .ws-prop-card {
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-md);
  overflow: hidden;
}
.pr-email-card-wrapper > .ws-prop-card--expanded { background: var(--ws-panel); }

/* Email draft body — DT/DD label grid, with matching subject/message inputs */
.pr-email-body { font-size: 13px; }
.pr-email-error {
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 4px;
  background: rgba(178, 52, 52, 0.08);
  color: var(--ws-err, #b23434);
  font-size: 12.5px;
}
.pr-email-fields {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 14px;
  margin: 0;
  align-items: center;
}
.pr-email-fields dt {
  font-family: var(--pr-font-mono);
  font-size: 10.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  font-weight: 500;
}
.pr-email-fields dd {
  margin: 0;
  color: var(--pr-ink);
  min-width: 0;
}
/* The textarea row is much taller than its label — pin the label to the
 * first line of the textarea instead of vertically centring it. */
.pr-email-fields .pr-email-label-multiline { align-self: start; padding-top: 7px; }
.pr-email-input {
  width: 100%;
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-sm);
  padding: 5px 9px;
  font-family: var(--ws-sans);
  font-size: 13px;
  color: var(--ws-ink);
  background: var(--ws-panel);
  line-height: 1.45;
}
.pr-email-input:focus {
  outline: none;
  border-color: var(--ws-ink-3);
}
.pr-email-input:disabled {
  background: var(--ws-bg);
  opacity: 0.7;
  cursor: not-allowed;
}
.pr-email-textarea {
  resize: vertical;
  min-height: 96px;
  padding: 7px 9px;
}
.pr-email-attachments {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pr-email-attachments li {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ws-text-muted, #6b7280);
}
.pr-wgroup-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.pr-wgroup-item {
  border-bottom: 1px solid var(--pr-hairline);
}
.pr-wgroup-item:last-child { border-bottom: none; }
.pr-wgroup-row {
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: var(--pr-ink);
  transition: background 120ms ease;
}
.pr-wgroup-row:hover { background: var(--pr-surface-2); }
.pr-wgroup-item.is-open .pr-wgroup-row { background: var(--pr-surface-2); }
.pr-wgroup-chev { color: var(--pr-ink-4); flex-shrink: 0; }
.pr-wgroup-icon { color: var(--pr-ink-4); flex-shrink: 0; }
.pr-wgroup-ident {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--pr-ink-2);
}
.pr-wgroup-pill {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10.5px;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: var(--pr-font-mono);
  font-weight: 500;
  flex-shrink: 0;
}
.pr-wgroup-pill--pending { background: var(--pr-surface); color: var(--pr-ink-3); border: 1px solid var(--pr-hairline); }
.pr-wgroup-pill--ok { background: var(--pr-success-soft); color: oklch(0.32 0.10 155); }
.pr-wgroup-pill--muted { background: var(--pr-surface); color: var(--pr-ink-4); border: 1px solid var(--pr-hairline); }
.pr-wgroup-pill--error { background: var(--pr-danger-soft); color: oklch(0.38 0.16 25); }
.pr-wgroup-body {
  padding: 6px 14px 12px;
  background: var(--pr-surface-2);
  border-top: 1px dotted var(--pr-hairline);
}
.pr-wgroup-body .pr-wcard { margin-left: 0; }
.pr-wgroup-body .pr-action-card { margin: 0; box-shadow: none; }

/* Write card field rows */
.pr-wcard { margin-left: 44px; }
.pr-wcard-fields {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 6px 18px;
  margin: 0;
  padding: 10px 0 2px;
  border-top: 1px dotted var(--pr-border);
  font-size: 13px;
}
.pr-wcard-fields dt {
  font-family: var(--pr-font-mono);
  font-size: 10.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  font-weight: 500;
  padding-top: 6px;
}
.pr-wcard-fields dd {
  margin: 0;
  color: var(--pr-ink);
  display: flex; align-items: center; gap: 6px;
}

/* Array-of-objects fields (e.g. order rows): a titled section whose items are
   indented (padding, not a left-border accent) and separated by a thin divider. */
.pr-objarray {
  grid-column: 1 / -1;
  padding-top: 8px;
}
.pr-objarray-title {
  font-family: var(--pr-font-mono);
  font-size: 10.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  font-weight: 500;
  margin-bottom: 6px;
}
.pr-objarray-item {
  padding: 7px 0 7px 14px;
  border-top: 1px dotted var(--pr-border);
}
.pr-objarray-item:first-of-type {
  border-top: none;
  padding-top: 0;
}
.pr-objarray-num {
  font-family: var(--pr-font-mono);
  font-size: 10px;
  color: var(--pr-ink-4);
  font-weight: 600;
  margin-bottom: 3px;
}
.pr-objarray-fields {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 3px 14px;
  margin: 0;
}
.pr-objarray-fields dt {
  font-family: var(--pr-font-mono);
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  font-weight: 500;
  padding-top: 1px;
}
.pr-objarray-fields dd {
  margin: 0;
  color: var(--pr-ink);
}

/* Segment draft preview (WriteCard body for entity=segment): audience-size
   banner + ALL / ANY / exclude criteria groups. */
.pr-segment-preview {
  padding: 10px 0 2px;
  border-top: 1px dotted var(--pr-border);
  font-size: 13px;
}
.pr-segment-audience-size {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  background: var(--pr-surface);
  border: 1px solid var(--pr-hairline);
  color: var(--pr-ink-3);
  font-size: 12.5px;
}
.pr-segment-audience-size strong {
  color: var(--pr-ink);
  font-weight: 600;
}
.pr-segment-group {
  padding-top: 8px;
}
.pr-segment-group:first-child {
  padding-top: 0;
}
.pr-segment-criterion {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 0 4px 14px;
  border-top: 1px dotted var(--pr-border);
}
.pr-segment-criterion:first-of-type {
  border-top: none;
}
.pr-segment-criterion-field {
  color: var(--pr-ink-3);
  font-weight: 500;
}
.pr-segment-criterion-op {
  color: var(--pr-ink-4);
}
.pr-segment-criterion-value {
  color: var(--pr-ink);
}

/* Shared small buttons */
.pr-btn {
  font-family: var(--pr-font-ui);
  font-size: 12.5px; font-weight: 500;
  padding: 7px 14px;
  border-radius: 3px;
  cursor: pointer;
  line-height: 1.2;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
  border: 1px solid transparent;
}
.pr-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.pr-btn-ghost {
  background: transparent;
  border-color: var(--pr-hairline);
  color: var(--pr-ink-3);
}
.pr-btn-ghost:hover:not(:disabled) { color: var(--pr-ink); border-color: var(--pr-border-2); }
.pr-btn-primary {
  background: var(--pr-ink);
  color: var(--pr-surface);
  border-color: var(--pr-ink);
}
.pr-btn-primary:hover:not(:disabled) { background: var(--pr-ink-2); border-color: var(--pr-ink-2); }
.pr-btn-primary.create { background: oklch(0.42 0.12 155); border-color: oklch(0.42 0.12 155); }
.pr-btn-primary.create:hover:not(:disabled) { background: oklch(0.37 0.13 155); border-color: oklch(0.37 0.13 155); }
.pr-btn-primary.delete { background: oklch(0.50 0.17 25); border-color: oklch(0.50 0.17 25); }
.pr-btn-primary.delete:hover:not(:disabled) { background: oklch(0.44 0.18 25); border-color: oklch(0.44 0.18 25); }

/* Side panel */
.pr-side-panel-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 14px 14px 20px;
  border-bottom: 1px solid var(--pr-hairline);
  flex-shrink: 0;
  background: var(--pr-bg);
}
.pr-side-panel-title {
  display: flex; align-items: baseline; gap: 8px;
  font-size: 13px; font-weight: 600; color: var(--pr-ink);
}
.pr-exhibit-tag {
  font-family: var(--pr-font-mono);
  font-size: 10px;
  color: var(--pr-ink-4);
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

/* Settings surface */
.pr-settings-wrap {
  flex: 1; min-width: 0;
  background: var(--pr-bg);
  overflow-y: auto;
  /* Reserve a constant gutter for the vertical scrollbar so switching tabs
     with different content heights doesn't shift the layout horizontally. */
  scrollbar-gutter: stable;
}
.pr-settings {
  max-width: 680px; margin: 0 auto;
  padding: 32px 28px 48px;
  display: flex; flex-direction: column; gap: 28px;
}
.pr-settings-card {
  background: var(--pr-surface);
  border: 1px solid var(--pr-hairline);
  border-radius: var(--pr-r-lg);
  overflow: hidden;
}
.pr-settings-nav {
  display: flex; gap: 4px;
  padding: 4px;
  background: var(--pr-surface-2);
  border-radius: var(--pr-r-md);
  border: 1px solid var(--pr-hairline);
  align-self: flex-start;
}
.pr-settings-nav button {
  padding: 7px 14px; border-radius: var(--pr-r-sm);
  background: transparent; border: none;
  font-size: 12.5px; font-weight: 500; color: var(--pr-ink-3);
  cursor: pointer;
  transition: all 120ms ease;
  font-family: inherit;
}
.pr-settings-nav button:hover { color: var(--pr-ink); }
.pr-settings-nav button.on {
  color: var(--pr-ink);
  background: var(--pr-surface);
  box-shadow: var(--pr-shadow-sm);
}
.pr-settings-section-head {
  display: flex; align-items: baseline; gap: 10px;
  margin-bottom: 12px;
  /* Match the 18px horizontal padding used by .pr-group-hdr so the section
     count lines up with the per-group counts inside the card. */
  padding: 0 18px;
}
.pr-icon-dot {
  width: 32px; height: 32px;
  border-radius: var(--pr-r-sm);
  background: var(--pr-accent-soft);
  color: var(--pr-accent);
  display: grid; place-items: center;
  flex-shrink: 0;
}
.pr-icon-dot svg { width: 18px; height: 18px; stroke-width: 1.7; }
.pr-settings-section-head .pr-icon-dot { align-self: center; }
.pr-settings-section-head h3 {
  margin: 0;
  font-family: var(--pr-font-serif);
  font-size: 20px; font-weight: 500; color: var(--pr-ink); letter-spacing: -0.015em;
}
.pr-settings-section-head .pr-count {
  margin-left: auto; font-size: 12px; color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
}

.pr-avatar-card {
  display: flex; align-items: center; gap: 12px;
  padding: 8px 12px;
}
.pr-avatar-card .pr-settings-btn {
  padding: 4px 10px;
  font-size: 12px;
}
.pr-avatar-card .pr-settings-btn svg { width: 12px; height: 12px; }
/* Keep the settings avatar visually identical to the header brand-dot */
.pr-avatar-lg {
  position: relative;
  width: 34px; height: 34px;
  border-radius: var(--pr-r-md);
  background: var(--pr-ink);
  display: grid; place-items: center;
  overflow: hidden;
  color: var(--pr-surface);
  font-family: var(--pr-font-serif); font-style: italic;
  font-weight: 500; font-size: 17px;
  letter-spacing: -0.01em;
  flex-shrink: 0;
}
.pr-avatar-lg img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pr-avatar-card .pr-meta { flex: 1; min-width: 0; }
.pr-avatar-card .pr-meta .pr-name {
  font-family: var(--pr-font-serif); font-weight: 500; font-size: 17px;
  color: var(--pr-ink); letter-spacing: -0.01em;
  line-height: 1.15;
}
.pr-avatar-card .pr-meta .pr-sub {
  font-size: 11.5px; color: var(--pr-ink-4);
  margin-top: 2px;
}

.pr-settings-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: var(--pr-r-sm);
  background: var(--pr-surface);
  border: 1px solid var(--pr-border);
  color: var(--pr-ink);
  cursor: pointer;
  font-size: 12.5px; font-weight: 500;
  transition: all 120ms ease;
  font-family: inherit;
}
.pr-settings-btn:hover { border-color: var(--pr-ink-4); background: var(--pr-surface-2); }
.pr-settings-btn.primary { background: var(--pr-ink); color: var(--pr-surface); border-color: var(--pr-ink); }
.pr-settings-btn.primary:hover { background: var(--pr-accent); border-color: var(--pr-accent); }
.pr-settings-btn.danger { background: oklch(0.56 0.18 25); color: var(--pr-surface); border-color: oklch(0.56 0.18 25); }
.pr-settings-btn.danger:hover { background: oklch(0.48 0.19 25); border-color: oklch(0.48 0.19 25); }

.pr-group-hdr {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 12px 18px;
  background: transparent; border: none;
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease;
  font-family: inherit;
}
.pr-group-hdr:hover { background: var(--pr-surface-2); }
.pr-subtle-hr { height: 1px; background: var(--pr-hairline); margin: 0; }
.pr-group-chevron { color: var(--pr-ink-4); transition: transform 150ms ease; flex-shrink: 0; display: grid; place-items: center; }
.pr-group-chevron svg { width: 14px; height: 14px; }
.pr-group-chevron.open { transform: rotate(90deg); }
.pr-group-tag { font-size: 13px; font-weight: 600; color: var(--pr-ink-2); letter-spacing: 0; }
.pr-group-count { margin-left: auto; font-size: 13px; color: var(--pr-ink-4); font-variant-numeric: tabular-nums; }

.pr-mem-row {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 10px 18px 10px 40px;
  border-top: 1px solid var(--pr-hairline);
}
.pr-mem-row .pr-mem-body { flex: 1; min-width: 0; }
.pr-mem-row .pr-mem-key {
  font-size: 11px; color: var(--pr-ink-4); margin-bottom: 3px;
  font-family: var(--pr-font-mono);
  font-weight: 500;
}
.pr-mem-row .pr-mem-val { font-size: 13.5px; color: var(--pr-ink); line-height: 1.55; white-space: pre-wrap; }
.pr-mem-row .pr-mem-tools { display: flex; gap: 2px; opacity: 0; transition: opacity 120ms ease; flex-shrink: 0; }
.pr-mem-row:hover .pr-mem-tools { opacity: 1; }
.pr-mem-row .pr-mem-tools .pr-iconbtn { width: 24px; height: 24px; }
.pr-conf {
  padding: 2px 7px; border-radius: 4px; font-size: 11px;
  font-variant-numeric: tabular-nums; font-weight: 500;
  flex-shrink: 0;
  font-family: var(--pr-font-mono);
}
.pr-conf.hi { color: var(--pr-success); background: var(--pr-success-soft); }
.pr-conf.mid { color: var(--pr-warn); background: var(--pr-warn-soft); }
.pr-conf.lo { color: var(--pr-ink-4); background: var(--pr-surface-2); }

.pr-dream-entry {
  padding: 14px 18px;
  border-bottom: 1px solid var(--pr-hairline);
}
.pr-dream-entry:last-child { border-bottom: none; }
.pr-dream-entry-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 6px;
}
.pr-dream-entry-date {
  font-family: var(--pr-font-mono);
  font-size: 11px;
  color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
}
.pr-dream-entry-tag {
  font-size: 10.5px; font-weight: 500;
  color: var(--pr-accent-ink);
  background: var(--pr-accent-soft);
  border: 1px solid var(--pr-accent-soft-2);
  padding: 1px 7px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.pr-dream-entry-body {
  font-family: var(--pr-font-serif);
  font-size: 14px;
  font-style: italic;
  line-height: 1.6;
  color: var(--pr-ink-2);
  white-space: pre-wrap;
}

/* Run grouping header — mirrors sidebar section heads so long run lists
   stay scannable (Today / Yesterday / This week / …). */
.pr-run-bucket-head {
  display: flex; align-items: center; gap: 8px;
  padding: 14px 0 6px;
  width: 100%;
  font-family: var(--pr-font-ui);
  font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pr-ink-4);
  border-bottom: 1px solid var(--pr-hairline);
  background: transparent; border-left: none; border-right: none; border-top: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: color 120ms ease;
}
.pr-run-bucket-head:hover { color: var(--pr-ink-3); }
.pr-run-bucket-chev {
  display: inline-flex; align-items: center; justify-content: center;
  transition: transform 140ms ease;
  color: var(--pr-ink-5);
}
.pr-run-bucket-chev.open { transform: rotate(90deg); }
.pr-run-bucket-label { flex: 1; }
.pr-run-bucket-count { font-weight: 500; text-transform: none; letter-spacing: 0; font-size: 11px; }

/* Schedule / hook header */
.pr-sched-header {
  display: flex; flex-direction: column;
  border: 1px solid var(--pr-hairline);
  border-radius: var(--pr-r-md);
  background: var(--pr-surface);
  box-shadow: var(--pr-shadow-sm);
  overflow: hidden;
}
.pr-sched-header-top {
  display: flex; align-items: center; gap: 6px;
  padding: 2px 6px;
  min-height: 28px;
  background: var(--pr-surface);
}
.pr-sched-header-top .pr-iconbtn { width: 24px; height: 24px; }
.pr-sched-header-top .pr-iconbtn svg { width: 14px; height: 14px; }
.pr-sched-trigger-tag {
  font-size: 10.5px; color: var(--pr-accent-ink);
  background: var(--pr-accent-soft);
  border: 1px solid var(--pr-accent-soft-2);
  padding: 1px 7px; border-radius: 999px;
  font-weight: 500;
  line-height: 1.5;
}
.pr-toggle {
  width: 28px; height: 16px; border-radius: 9px;
  background: var(--pr-surface-3); position: relative;
  cursor: pointer; transition: background 150ms ease;
  flex-shrink: 0;
  border: 1px solid var(--pr-border);
  padding: 0;
}
.pr-toggle .pr-tknob {
  position: absolute; top: 1px; left: 1px;
  width: 12px; height: 12px; border-radius: 50%;
  background: var(--pr-surface);
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
  transition: transform 150ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.pr-toggle.on { background: var(--pr-accent); border-color: var(--pr-accent); }
.pr-toggle.on .pr-tknob { transform: translateX(12px); }
.pr-retry-row { display: flex; justify-content: flex-end; }

/* ThinkingBlock — Claude-style: collapsed one-sentence summary, expandable timeline */
.pr-think {
  margin: 2px 0 8px;
}
.pr-think-summary {
  display: flex; align-items: center; gap: 6px;
  max-width: 100%; text-align: left;
  background: none; border: 0; padding: 0; cursor: pointer;
  color: var(--pr-ink-4); font-size: 13px; line-height: 1.5;
}
.pr-think-summary:hover { color: var(--pr-ink-3); }
.pr-think-summary-text {
  min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.pr-think-summary-icon { flex-shrink: 0; color: var(--pr-ink-4); }
.pr-think-chev {
  flex-shrink: 0; opacity: 0.7;
  transition: transform 150ms ease;
}
.pr-think-chev-open { transform: rotate(90deg); }

/* Expanded reasoning — the full text, bounded and scrollable. */
.pr-think-full {
  font-size: 13px; color: var(--pr-ink-3); line-height: 1.55;
  white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere;
  margin-top: 6px; padding-left: 20px;
  max-height: 320px; overflow-y: auto;
}
.pr-think-detail { margin-top: 2px; }
.pr-think-done {
  display: flex; align-items: center; gap: 6px;
  margin-top: 10px; padding-left: 3px;
  font-size: 13px; color: var(--pr-ink-4); line-height: 1.5;
}
.pr-think-done svg { flex-shrink: 0; }
.pr-think-pulse { animation: pr-think-pulse 1.6s ease-in-out infinite; }
@keyframes pr-think-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}
`;

export default praxisStyles;
